<div class="pane-layout">
  <!-- Header -->
  <header class="pane-header">
    <div class="header-content">
      <div class="ai-icon">
        <mat-icon>assistant</mat-icon>
      </div>
      <div class="header-text">
        <h3>{{ 'GenerateContent.AssistantTitle' | translate }}</h3>
        <p>{{ 'GenerateContent.AssistantSubtitle' | translate }}</p>
      </div>
    </div>
    <div class="header-actions">
      <button
        #clearConvTrigger
        mat-icon-button
        (click)="openClearConversationMenu($event, clearConvTrigger)"
        [disabled]="!conversation()?.messages?.length"
        [matTooltip]="'GenerateContent.ClearConversation' | translate"
      >
        <mat-icon>delete_sweep</mat-icon>
      </button>
      <button mat-icon-button (click)="closed.emit(null)" [attr.aria-label]="'GenerateContent.Close' | translate" [matTooltip]="'GenerateContent.Close' | translate">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </header>

  <!-- Chat Area -->
  <div class="chat-area custom-scrollbar" #scrollContainer>
    <!-- Empty State / Welcome -->
    @if (!processedConversation()?.messages?.length && !loading()) {
      <div class="welcome-container">
        <div class="welcome-message">
          <p>{{ 'GenerateContent.Welcome.Intro' | translate }}</p>
          <ul>
            <li>{{ 'GenerateContent.Welcome.Capability1' | translate }}</li>
            <li>{{ 'GenerateContent.Welcome.Capability2' | translate }}</li>
            <li>{{ 'GenerateContent.Welcome.Capability3' | translate }}</li>
            <li>{{ 'GenerateContent.Welcome.Capability4' | translate }}</li>
            <li>{{ 'GenerateContent.Welcome.Capability5' | translate }}</li>
          </ul>
          <p class="mt-3">{{ 'GenerateContent.Welcome.HowToHelp' | translate }}</p>
          <div class="timestamp">{{ now | date: 'HH:mm' }}</div>
        </div>

        <!-- Quick Actions -->
        @if (!isReader()) {
          @if (!cannotUseAI()) {
            <div class="quick-actions">
              <p class="quick-actions-title">{{ 'GenerateContent.QuickActions.Title' | translate }}:</p>
              <button mat-button class="action-btn" (click)="startGenerateContent()">
                <mat-icon>description</mat-icon>
                {{ 'GenerateContent.QuickActions.GenerateContent' | translate }}
              </button>
              <button mat-button class="action-btn" (click)="toggleMode('image')">
                <mat-icon>image</mat-icon>
                {{ 'GenerateContent.QuickActions.CreateImage' | translate }}
              </button>
              <button mat-button class="action-btn" (click)="toggleMode('quiz')">
                <mat-icon>grading</mat-icon>
                {{ 'GenerateContent.QuickActions.CreateQuiz' | translate }}
              </button>
              <button mat-button class="action-btn" (click)="generateMermaid()">
                <mat-icon>account_tree</mat-icon>
                {{ 'GenerateContent.QuickActions.CreateMermaid' | translate }}
              </button>
            </div>
          }
        }
      </div>
    }

    <!-- Messages -->
    <div class="messages-list">
      @for (message of processedConversation()?.messages; track $index) {
        @if (message.role !== 'system') {
          @switch (message.type) {
            @case ('text') {
              <div class="message-bubble" [class.user]="message.role === 'user'" [class.assistant]="message.role === 'assistant'">
                <markdown class="bubble-content" [data]="message.content"></markdown>
                @if (message.files?.length) {
                  <div class="message-attachments">
                    @for (file of message.files; track $index) {
                      <div class="attachment-pill" (click)="downloadAttachment(file)">
                        <mat-icon class="attachment-icon">attachment</mat-icon>
                        <span class="attachment-name" [matTooltip]="getFileNameFromUrl(file)">{{ getFileNameFromUrl(file) }}</span>
                      </div>
                    }
                  </div>
                }
                @if (message.role === 'assistant' && !isReader()) {
                  <div class="message-actions">
                    <button mat-button (click)="createChunk(message.content)">
                      <mat-icon>arrow_insert</mat-icon>
                      {{ 'GenerateContent.Insert' | translate }}
                    </button>
                  </div>
                }
              </div>
            }
            @case ('image_request') {
              <div class="message-bubble user">
                <div class="bubble-content">{{ message.content }}</div>
                @if (message.files?.length) {
                  <div class="message-attachments">
                    @for (file of message.files; track $index) {
                      <div class="attachment-pill" (click)="downloadAttachment(file)">
                        <mat-icon class="attachment-icon">attachment</mat-icon>
                        <span class="attachment-name" [matTooltip]="getFileNameFromUrl(file)">{{ getFileNameFromUrl(file) }}</span>
                      </div>
                    }
                  </div>
                }
              </div>
            }
            @case ('image') {
              <div class="message-bubble assistant">
                <div class="bubble-content">
                  <app-image-viewer [src]="message.content.displayUrl" [alt]="message.content.prompt" borderRadius="12px" objectFit="contain"></app-image-viewer>
                  @if (!isReader()) {
                    <div class="message-actions">
                      <button mat-button (click)="uploadImageAndCreateChunk(message.content.imageUrl, message.content.prompt)">
                        <mat-icon>arrow_insert</mat-icon>
                        {{ 'GenerateContent.InsertImage' | translate }}
                      </button>
                    </div>
                  }
                </div>
              </div>
            }

            @case ('quiz_request') {
              <div class="message-bubble user">
                <div class="bubble-content">
                  {{ message.content }}
                </div>
                @if (message.files?.length) {
                  <div class="message-attachments">
                    @for (file of message.files; track $index) {
                      <div class="attachment-pill" (click)="downloadAttachment(file)">
                        <mat-icon class="attachment-icon">attachment</mat-icon>
                        <span class="attachment-name" [matTooltip]="getFileNameFromUrl(file)">{{ getFileNameFromUrl(file) }}</span>
                      </div>
                    }
                  </div>
                }
              </div>
            }
            @case ('quiz') {
              <div class="message-bubble assistant quiz-bubble">
                <app-quiz [data]="message.content" [contentID]="contentID()" (insert)="handleInsert($event)"></app-quiz>
                @if (!isReader()) {
                  <div class="message-actions">
                    <button mat-button (click)="handleInsert(message.content)">
                      <mat-icon>arrow_insert</mat-icon>
                      {{ 'GenerateContent.Insert' | translate }}
                    </button>
                  </div>
                }
              </div>
            }
            @case ('content_request') {
              <div class="message-bubble user">
                <div class="bubble-content">
                  <strong>{{ 'GenerateContent.RequestLabel' | translate }}:</strong>
                  {{ message.content }}
                </div>
                @if (message.files?.length) {
                  <div class="message-attachments">
                    @for (file of message.files; track $index) {
                      <div class="attachment-pill" (click)="downloadAttachment(file)">
                        <mat-icon class="attachment-icon">attachment</mat-icon>
                        <span class="attachment-name" [matTooltip]="getFileNameFromUrl(file)">{{ getFileNameFromUrl(file) }}</span>
                      </div>
                    }
                  </div>
                }
              </div>
            }
            @case ('content_response') {
              <div class="message-bubble assistant canvas-bubble">
                <div class="canvas-card" (click)="openCanvas(message)">
                  <div class="icon">
                    <mat-icon>article</mat-icon>
                  </div>
                  <div class="info">
                    <h4>{{ message.content.title }}</h4>
                    <p>{{ 'GenerateContent.ClickToView' | translate }}</p>
                  </div>
                </div>
              </div>
            }
            @case ('mermaid_request') {
              <div class="message-bubble user">
                <div class="bubble-content">
                  <strong>{{ 'GenerateContent.Mermaid.RequestLabel' | translate }}:</strong>
                  {{ message.content }}
                </div>
              </div>
            }
            @case ('mermaid') {
              <div class="message-bubble assistant mermaid-bubble">
                <div class="mermaid-card" (click)="openMermaidPreview(message)">
                  <div class="icon">
                    <mat-icon>account_tree</mat-icon>
                  </div>
                  <div class="info">
                    <h4>{{ message.content.title }}</h4>
                    <p>{{ 'GenerateContent.ClickToView' | translate }}</p>
                  </div>
                </div>
                @if (!isReader()) {
                  <div class="message-actions">
                    <button mat-button (click)="insertMermaidFromMessage(message)">
                      <mat-icon>arrow_insert</mat-icon>
                      {{ 'GenerateContent.Insert' | translate }}
                    </button>
                  </div>
                }
              </div>
            }
          }
        }
      }

      @if (loading()) {
        <div class="message-bubble assistant loading">
          <mat-spinner diameter="24"></mat-spinner>
        </div>
      }
    </div>
  </div>

  <!-- Input Area -->
  <div class="input-area" [class.disabled-ai]="cannotUseAI()" (dragover)="onDragOver($event)" (dragenter)="onDragEnter($event)" (dragleave)="onDragLeave($event)" (drop)="onDrop($event)">
    <div class="input-container" [class.mermaid-mode]="activeInputMode === 'mermaid' || activeInputMode === 'image' || activeInputMode === 'content' || activeInputMode === 'quiz'">
      @if (cannotUseAI()) {
        <div class="no-ai-overlay" [matTooltip]="'GenerateContent.NoAiAccessTooltip' | translate" appAiRestrictedHover>
          <mat-icon>lock</mat-icon>
          <p>{{ 'GenerateContent.NoAiAccess' | translate }}</p>
        </div>
      }
      @if (isDraggingOver()) {
        <div class="drop-overlay">
          <div class="drop-content">
            <mat-icon>cloud_upload</mat-icon>
            <p>{{ 'FileUploader.DropFileHere' | translate }}</p>
          </div>
        </div>
      }

      <!-- Floating Model Selector (hidden in mermaid, image, content, and quiz modes) -->
      @if (activeInputMode !== 'mermaid' && activeInputMode !== 'settings' && activeInputMode !== 'image' && activeInputMode !== 'content' && activeInputMode !== 'quiz') {
        <div class="model-selector-floating">
          @if (activeInputMode === 'text') {
            @if (useContentContext() && contentName()) {
              <div class="context-chip">
                <span class="context-label">{{ contentName() }}</span>
                @if (!isReader() && !cannotUseAI()) {
                  <button mat-icon-button class="remove-context" (click)="toggleContext()">
                    <mat-icon>close</mat-icon>
                  </button>
                }
              </div>
            }

            @for (ext of attachedExternalContexts(); track ext.id) {
              <div class="context-chip external-context" matTooltip="{{ ext.title }}">
                <mat-icon class="context-icon opacity-70 scale-[0.8]">
                  {{ ext.type === 'lecture' ? 'school' : ext.type === 'note' ? 'sticky_note_2' : 'article' }}
                </mat-icon>
                <span class="context-label">{{ ext.name }}</span>
                <button mat-icon-button class="remove-context" (click)="removeExternalContext(ext.id)">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            }

            @if (!useContentContext() && attachedExternalContexts().length === 0) {
              <div class="context-chip disabled cursor-pointer bg-[#f1f3f4] text-[#5f6368]" (click)="toggleContext()">
                <mat-icon class="context-icon">visibility_off</mat-icon>
                <span class="context-label">{{ 'GenerateContent.ContextHidden' | translate }}</span>
              </div>
            }
          }
        </div>
      }

      @if (activeInputMode !== 'mermaid' && activeInputMode !== 'settings' && activeInputMode !== 'image' && activeInputMode !== 'content' && activeInputMode !== 'quiz') {
        @if (attachedFiles() && attachedFiles().length > 0) {
          <div class="attached-files-container">
            @for (file of attachedFiles(); track file.id) {
              <div class="media-context-card">
                <div class="icon-wrapper">
                  @if (file.uploading) {
                    <mat-spinner diameter="20"></mat-spinner>
                  } @else {
                    <mat-icon>attachment</mat-icon>
                  }
                </div>
                <div class="info-wrapper">
                  <h4 [matTooltip]="file.name">{{ file.name }}</h4>
                  @if (file.uploading) {
                    <p>{{ 'FileUploader.Uploading' | translate }}...</p>
                  } @else {
                    <p>{{ 'ContentGenerator.FileAttached' | translate }}</p>
                  }
                </div>
                <button mat-icon-button class="remove-btn" (click)="removeAttachment(file.id)">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            }
          </div>
        }

        <quill-editor
          class="chat-input-editor"
          [(ngModel)]="prompt"
          [modules]="quillModules"
          [format]="'text'"
          [readOnly]="cannotUseAI()"
          [placeholder]="(cannotUseAI() ? 'GenerateContent.NoAiAccess' : 'GenerateContent.PromptPlaceholder') | translate"
          (keydown)="onEditorKeyDown($event)"
        ></quill-editor>

        <div class="input-actions">
          <!-- Left Actions -->
          <div class="left-actions">
            @if (!isReader() && !cannotUseAI()) {
              <!-- Group 1: Join file and import context -->
              <div class="action-group">
                <button mat-icon-button (click)="fileInput.click()" [matTooltip]="'ContentGenerator.AttachFile' | translate">
                  <mat-icon>attach_file</mat-icon>
                </button>
                <input type="file" #fileInput hidden multiple (change)="onFileSelected($event)" />

                <button mat-icon-button (click)="toggleContentSearcher()" [matTooltip]="'Searcher.AttachLibraryContent' | translate">
                  <mat-icon>docs_add_on</mat-icon>
                </button>
              </div>

              <!-- Group 2: Generate content -->
              <div class="action-group">
                <button mat-icon-button (click)="toggleMode('image')" [class.active]="false" [matTooltip]="'GenerateContent.Modes.Image' | translate">
                  <mat-icon>image</mat-icon>
                </button>

                <button mat-icon-button (click)="startGenerateContent()" [class.active]="false" [matTooltip]="'GenerateContent.QuickActions.GenerateContent' | translate">
                  <mat-icon>description</mat-icon>
                </button>
                <button mat-icon-button (click)="toggleMode('quiz')" [class.active]="false" [matTooltip]="'GenerateContent.QuickActions.CreateQuiz' | translate">
                  <mat-icon>grading</mat-icon>
                </button>
                <button mat-icon-button (click)="generateMermaid()" [class.active]="false" [matTooltip]="'GenerateContent.QuickActions.CreateMermaid' | translate">
                  <mat-icon>account_tree</mat-icon>
                </button>
              </div>

              <!-- Group 3: Settings -->
              @if (!isReader()) {
                <div class="action-group last-group">
                  <button mat-icon-button (click)="toggleSettingsMode()" [matTooltip]="'GenerateContent.Settings' | translate">
                    <mat-icon>tune</mat-icon>
                  </button>
                </div>
              }
            }
          </div>

          <!-- Spacer -->
          <div class="spacer"></div>

          <!-- Send Button -->
          <button mat-fab color="primary" class="send-btn" (click)="handleGenerateClick()" [disabled]="cannotUseAI() || loading() || !prompt?.trim() || !allFilesFullyUploaded()">
            <mat-icon>send</mat-icon>
          </button>
        </div>
      } @else if (activeInputMode === 'image') {
        <!-- Image Inline Form -->
        <div class="image-inline-form" [formGroup]="imageForm">
          <!-- Description (Prompt) -->
          <div class="w-full mb-2 flex flex-col gap-1 p-1">
            <span class="text-xs font-semibold text-[#5f6368]">{{ 'ImageGenerator.PromptLabel' | translate }} *</span>
            <textarea
              class="w-full rounded-[10px] bg-white border border-[#e0e0e0] px-3 py-2 text-sm outline-none resize-none placeholder:text-[#9ca3af] placeholder:font-semibold focus:border-[#5a73c5] focus:ring-1 focus:ring-[#5a73c5] transition-all"
              formControlName="prompt"
              rows="2"
              [placeholder]="'ImageGenerator.PromptPlaceholder' | translate"
            ></textarea>
          </div>

          <!-- Selects Row: Format & Model -->
          <div class="grid grid-cols-2 gap-3 p-1">
            <div class="flex flex-col gap-1 min-w-0">
              <span class="text-xs font-semibold text-[#5f6368]">{{ 'ImageGenerator.Format' | translate }}</span>
              <ui-select class="w-full" [compact]="true" formControlName="aspectRatio" [options]="aspectRatioOptions" [placeholder]="'ImageGenerator.Format' | translate" />
            </div>
            <div class="flex flex-col gap-1 min-w-0">
              <span class="text-xs font-semibold text-[#5f6368]">{{ 'ImageGenerator.ModelLabel' | translate }}</span>
              <ui-select class="w-full" [compact]="true" formControlName="model" [options]="imageModelOptions" [placeholder]="'ImageGenerator.ModelLabel' | translate" />
            </div>
          </div>

          <!-- Actions row -->
          <div class="flex items-center justify-end gap-2 mt-3">
            <button mat-button (click)="cancelImage()" class="px-3 py-1.5 rounded-[8px] text-sm font-semibold text-[#5f6368] hover:bg-[#f1f3f4] transition-colors">
              {{ 'Global.Cancel' | translate }}
            </button>
            <button mat-flat-button color="primary" btnType="action" [disabled]="imageForm.invalid || loading()" (click)="submitImage()" class="rounded-[12px] font-semibold text-sm px-4">
              {{ 'ImageGenerator.GenerateAction' | translate }}
            </button>
          </div>
        </div>
      } @else if (activeInputMode === 'content') {
        <!-- Content Inline Form -->
        <div class="content-inline-form" [formGroup]="contentForm">
          <!-- Subject -->
          <div class="w-full mb-2 flex flex-col gap-1 p-1">
            <span class="text-xs font-semibold text-[#5f6368]">{{ 'ContentGenerator.AiSubjectLabel' | translate }} *</span>
            <textarea
              class="w-full rounded-[10px] bg-white border border-[#e0e0e0] px-3 py-2 text-sm outline-none resize-none placeholder:text-[#9ca3af] placeholder:font-semibold focus:border-[#5a73c5] focus:ring-1 focus:ring-[#5a73c5] transition-all"
              formControlName="subject"
              rows="3"
              [placeholder]="'ContentGenerator.AiSubjectPlaceholder' | translate"
            ></textarea>
          </div>

          <!-- Customization -->
          <div class="w-full mb-2 flex flex-col gap-1 p-1">
            <span class="text-xs font-semibold text-[#5f6368]">{{ 'ContentGenerator.AiCustomizationLabel' | translate }}</span>
            <textarea
              class="w-full rounded-[10px] bg-white border border-[#e0e0e0] px-3 py-2 text-sm outline-none resize-none placeholder:text-[#9ca3af] placeholder:font-semibold focus:border-[#5a73c5] focus:ring-1 focus:ring-[#5a73c5] transition-all"
              formControlName="customization"
              rows="2"
              [placeholder]="'ContentGenerator.CustomizationHint' | translate"
            ></textarea>
          </div>

          <!-- Selects Row: Model & Template -->
          <div class="grid grid-cols-2 gap-3 p-1 items-end">
            <div class="flex flex-col gap-1 min-w-0">
              <span class="text-xs font-semibold text-[#5f6368]">{{ 'ContentGenerator.AiModelLabel' | translate }}</span>
              <ui-select class="w-full" [compact]="true" formControlName="model" [options]="contentModelOptions" [placeholder]="'ContentGenerator.AiModelLabel' | translate" />
            </div>
            <div class="flex flex-col gap-1 min-w-0">
              <app-template-picker
                [selectedTextGenerationTemplateID]="contentForm.get('template')?.value"
                (onAIGenerationTemplateChange)="contentForm.patchValue({ template: $event })"
              ></app-template-picker>
            </div>
          </div>

          <!-- Actions row -->
          <div class="flex items-center justify-end gap-2 mt-3">
            <button mat-button (click)="cancelContent()" class="px-3 py-1.5 rounded-[8px] text-sm font-semibold text-[#5f6368] hover:bg-[#f1f3f4] transition-colors">
              {{ 'Global.Cancel' | translate }}
            </button>
            <button mat-flat-button color="primary" btnType="action" [disabled]="contentForm.invalid || loading()" (click)="submitContent()" class="rounded-[12px] font-semibold text-sm px-4">
              {{ 'Global.Generate' | translate }}
            </button>
          </div>
        </div>
      } @else if (activeInputMode === 'quiz') {
        <!-- Quiz Inline Form -->
        <div class="quiz-inline-form" [formGroup]="quizForm">
          <!-- Subject -->
          <div class="w-full mb-2 flex flex-col gap-1 p-1">
            <span class="text-xs font-semibold text-[#5f6368]">{{ 'Quiz.AiSubjectLabel' | translate }} *</span>
            <textarea
              class="w-full rounded-[10px] bg-white border border-[#e0e0e0] px-3 py-2 text-sm outline-none resize-none placeholder:text-[#9ca3af] placeholder:font-semibold focus:border-[#5a73c5] focus:ring-1 focus:ring-[#5a73c5] transition-all"
              formControlName="subject"
              rows="3"
              [placeholder]="'Quiz.AiSubjectPlaceholder' | translate"
            ></textarea>
          </div>

          <!-- Selects Row: Count & Type -->
          <div class="grid grid-cols-2 gap-3 p-1">
            <div class="flex flex-col gap-1 min-w-0">
              <span class="text-xs font-semibold text-[#5f6368]">{{ 'Quiz.AiCountLabel' | translate }}</span>
              <ui-input [compact]="true" type="number" formControlName="count" />
            </div>
            <div class="flex flex-col gap-1 min-w-0">
              <span class="text-xs font-semibold text-[#5f6368]">{{ 'Quiz.AiTypeLabel' | translate }}</span>
              <ui-select class="w-full" [compact]="true" formControlName="quizType" [options]="quizTypeOptions" [placeholder]="'Quiz.AiTypeLabel' | translate" />
            </div>
          </div>

          <!-- Difficulty buttons -->
          <div class="w-full mb-2 flex flex-col gap-1 p-1">
            <span class="text-xs font-semibold text-[#5f6368]">{{ 'Quiz.AiDifficultyLabel' | translate }}</span>
            <div class="flex gap-2 w-full">
              <button
                type="button"
                (click)="quizForm.patchValue({ difficulty: 'easy' })"
                class="flex-1 py-1 text-sm font-semibold rounded-[8px] border transition-all"
                [class]="quizForm.get('difficulty')?.value === 'easy' ? 'bg-[#5a73c5] border-[#5a73c5] text-white' : 'bg-white border-[#e0e0e0] text-[#5f6368] hover:bg-[#f1f3f4]'"
              >
                {{ 'Quiz.DifficultyEasy' | translate }}
              </button>
              <button
                type="button"
                (click)="quizForm.patchValue({ difficulty: 'medium' })"
                class="flex-1 py-1 text-sm font-semibold rounded-[8px] border transition-all"
                [class]="quizForm.get('difficulty')?.value === 'medium' ? 'bg-[#5a73c5] border-[#5a73c5] text-white' : 'bg-white border-[#e0e0e0] text-[#5f6368] hover:bg-[#f1f3f4]'"
              >
                {{ 'Quiz.DifficultyMedium' | translate }}
              </button>
              <button
                type="button"
                (click)="quizForm.patchValue({ difficulty: 'hard' })"
                class="flex-1 py-1 text-sm font-semibold rounded-[8px] border transition-all"
                [class]="quizForm.get('difficulty')?.value === 'hard' ? 'bg-[#5a73c5] border-[#5a73c5] text-white' : 'bg-white border-[#e0e0e0] text-[#5f6368] hover:bg-[#f1f3f4]'"
              >
                {{ 'Quiz.DifficultyHard' | translate }}
              </button>
            </div>
          </div>

          <!-- Additional Context -->
          <div class="w-full mb-2 flex flex-col gap-1 p-1">
            <span class="text-xs font-semibold text-[#5f6368]">{{ 'Quiz.AiContextLabel' | translate }}</span>
            <textarea
              class="w-full rounded-[10px] bg-white border border-[#e0e0e0] px-3 py-2 text-sm outline-none resize-none placeholder:text-[#9ca3af] placeholder:font-semibold focus:border-[#5a73c5] focus:ring-1 focus:ring-[#5a73c5] transition-all"
              formControlName="additionalContext"
              rows="2"
              [placeholder]="'Quiz.AiContextPlaceholder' | translate"
            ></textarea>
          </div>

          <!-- Actions row -->
          <div class="flex items-center justify-end gap-2 mt-3">
            <button mat-button (click)="cancelQuiz()" class="px-3 py-1.5 rounded-[8px] text-sm font-semibold text-[#5f6368] hover:bg-[#f1f3f4] transition-colors">
              {{ 'Global.Cancel' | translate }}
            </button>
            <button mat-flat-button color="primary" btnType="action" [disabled]="quizForm.invalid || loading()" (click)="submitQuiz()" class="rounded-[12px] font-semibold text-sm px-4">
              {{ 'Quiz.GenerateAction' | translate }}
            </button>
          </div>
        </div>
      } @else if (activeInputMode === 'mermaid') {
        <!-- Mermaid Inline Form -->
        <div class="mermaid-inline-form" [formGroup]="mermaidForm">
          <!-- Subject -->
          <div class="w-full mb-2 flex flex-col gap-1 p-1">
            <span class="text-xs font-semibold text-[#5f6368]">{{ 'GenerateContent.Mermaid.Subject' | translate }}</span>
            <textarea
              class="w-full rounded-[10px] bg-white border border-[#e0e0e0] px-3 py-2 text-sm outline-none resize-none placeholder:text-[#9ca3af] placeholder:font-semibold focus:border-[#5a73c5] focus:ring-1 focus:ring-[#5a73c5] transition-all"
              formControlName="subject"
              rows="2"
              [placeholder]="'GenerateContent.Mermaid.SubjectPlaceholder' | translate"
            ></textarea>
          </div>

          <!-- Customization -->
          <div class="w-full mb-2 flex flex-col gap-1 p-1">
            <span class="text-xs font-semibold text-[#5f6368]">{{ 'GenerateContent.Mermaid.Customization' | translate }}</span>
            <textarea
              class="w-full rounded-[10px] bg-white border border-[#e0e0e0] px-3 py-2 text-sm outline-none resize-none placeholder:text-[#9ca3af] placeholder:font-semibold focus:border-[#5a73c5] focus:ring-1 focus:ring-[#5a73c5] transition-all"
              formControlName="customization"
              rows="2"
              [placeholder]="'GenerateContent.Mermaid.CustomizationPlaceholder' | translate"
            ></textarea>
          </div>

          <!-- Inline selects row -->
          <div class="grid grid-cols-2 gap-3 p-1">
            <div class="flex flex-col gap-1 min-w-0">
              <span class="text-xs font-semibold text-[#5f6368]">{{ 'GenerateContent.Mermaid.DiagramType' | translate }}</span>
              <ui-select class="w-full" [compact]="true" formControlName="diagramType" [options]="diagramTypeOptions" [placeholder]="'GenerateContent.Mermaid.TypeAuto' | translate" />
            </div>
            <div class="flex flex-col gap-1 min-w-0">
              <span class="text-xs font-semibold text-[#5f6368]">{{ 'GenerateContent.Mermaid.Model' | translate }}</span>
              <ui-select class="w-full" [compact]="true" formControlName="model" [options]="modelOptions" [placeholder]="'GenerateContent.Mermaid.ModelFast' | translate" />
            </div>
          </div>

          <!-- Actions row -->
          <div class="flex items-center justify-end gap-2 mt-3">
            <button mat-button (click)="cancelMermaid()" class="px-3 py-1.5 rounded-[8px] text-sm font-semibold text-[#5f6368] hover:bg-[#f1f3f4] transition-colors">
              {{ 'Global.Cancel' | translate }}
            </button>
            <button mat-flat-button color="primary" btnType="action" [disabled]="mermaidForm.invalid || loading()" (click)="submitMermaid()" class="rounded-[12px] font-semibold text-sm px-4">
              {{ 'GenerateContent.Mermaid.Generate' | translate }}
            </button>
          </div>
        </div>
      } @else if (activeInputMode === 'settings') {
        <!-- Settings Inline Form -->
        <div class="ps-0 py-2 pe-2 w-full">
          <div class="flex flex-col gap-4 p-2">
            <div class="flex items-center justify-between gap-2">
              <div class="flex flex-col gap-1">
                <span class="text-sm font-semibold text-[#3c4043]">{{ 'GenerateContent.AllowAiSearch' | translate }}</span>
                <span class="text-xs font-semibold text-[#9ca3af] max-w-[300px]">{{ 'GenerateContent.AllowAiSearchDesc' | translate }}</span>
              </div>
              <ui-checkbox [checked]="allowAiSearch()" [disabled]="isReader()" (checkedChange)="toggleAiSearch($event)"></ui-checkbox>
            </div>

            <div class="flex items-center justify-between gap-2">
              <div class="flex flex-col gap-1">
                <span class="text-sm font-semibold text-[#3c4043]">{{ 'GenerateContent.SearchAllContent' | translate }}</span>
                <span class="text-xs font-semibold text-[#9ca3af] max-w-[300px]">{{ 'GenerateContent.SearchAllContentDesc' | translate }}</span>
              </div>
              <ui-checkbox [checked]="searchAllContent()" [disabled]="isReader()" (checkedChange)="toggleSearchAllContent($event)"></ui-checkbox>
            </div>

            <div class="flex items-center justify-between gap-2">
              <div class="flex flex-col gap-1">
                <span class="text-sm font-semibold text-[#3c4043]">{{ 'GenerateContent.RestrictToSkillztank' | translate }}</span>
                <span class="text-xs font-semibold text-[#9ca3af] max-w-[300px]">{{ 'GenerateContent.RestrictToSkillztankDesc' | translate }}</span>
              </div>
              <ui-checkbox [checked]="aiSkillztankBoundary()" [disabled]="isReader()" (checkedChange)="toggleAiSkillztankBoundary($event)"></ui-checkbox>
            </div>

            <div class="flex items-center justify-end mt-2">
              <button mat-flat-button color="primary" btnType="action" (click)="toggleSettingsMode()" class="rounded-[12px] font-semibold text-sm px-5 py-1">
                {{ 'Global.Done' | translate }}
              </button>
            </div>
          </div>
        </div>
      }
    </div>
  </div>
</div>
